import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router"
import { forkJoin, Subscription, takeUntil } from 'rxjs';
import { Role } from '@models/role.model';
import { UploadBatchState } from '@models/ui/case-analytics-state/upload-batch-state.model';
import { ProductionJobState } from '@models/ui/case-analytics-state/production-job-state.model';
import { ReviewSetState } from '@models/ui/case-analytics-state/review-set-state-model';
import { NotificationManager } from '@managers/notification.manager';
import { BaseComponent } from '../../base.component';
import { HelpService } from '@services/help.service';
import { UserConfigsService } from '@services/user-configs.service';
import { StateSdk } from '@sdks/state.sdk';
import { ReviewDocumentState } from '@models/ui/case-analytics-state/review-document-state.model';
import { AnalysisManager } from "@managers/analysis.manager";
import { CustodianState } from '@models/ui/case-analytics-state/custodian-state.model';
import { CollectionSummaryState } from '@models/ui/case-analytics-state/collection-summary-state.model';
import { appear } from './animations/appear';

@Component({
    selector: 'lex-case-analytics',
    templateUrl: './case-analytics.component.html',
    styleUrls: ['./case-analytics.component.scss'],
    animations: [
        appear
    ],
    standalone: false
})
export class CaseAnalyticsComponent extends BaseComponent implements OnInit, OnDestroy {
	public uploadBatchState: UploadBatchState = null;
	public reviewSetState: ReviewSetState = null;
	public productionJobState: ProductionJobState = null
	public custodianState: CustodianState = null;
	public reviewDocumentState: ReviewDocumentState = null;
	public collectionData: CollectionSummaryState = null;
	public userFirstName: string = "";
	public isLoading: boolean = true;
	public showButtonHoverEffect: boolean = false;
	private subscription = new Subscription();

	constructor(
		private helpService: HelpService,
		private userConfigsService: UserConfigsService,
		private router: Router,
		private stateSdk: StateSdk,
		private notificationManager: NotificationManager,
		private analysisManager: AnalysisManager
	) {
		super();
		forkJoin({
			reviewDocumentState: this.analysisManager.getCaseReviewDocumentAnalyticsData(),
			custodianState: this.analysisManager.getCustodianAnalyticsData(),
			collectionSummaryData: this.analysisManager.getCollectionSummaryAnalyticsData(),
			caseAnalyticsState: this.stateSdk.getCaseAnalyticsState()
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.collectionData = response.collectionSummaryData;
					this.custodianState = response.custodianState;
					this.reviewDocumentState = response.reviewDocumentState;
					const caseAnalyticsState = response.caseAnalyticsState;

					this.userFirstName = caseAnalyticsState.userFirstName;
					this.reviewSetState = caseAnalyticsState.reviewSetState;
					this.productionJobState = caseAnalyticsState.productionJobState;
					this.uploadBatchState = caseAnalyticsState.uploadBatchState;
					this.isLoading = false;
				},
				error: () => {
					this.notificationManager.displaySnackBarMessage({
						message: 'Something went wrong when loading case analytics. Please contact Professional Services for support.',
						type: 'error',
						ttl: 5000
					});
					this.router.navigate(['/', 'dashboard']);
				}
			});
	}

	ngOnInit(): void {
		/*TO DO: Need a case analytics help page*/
		this.helpService.updateHelpLink('analysis-menu/case-assessment');
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public isReviewSetUser(): boolean {
		return this.userConfigsService.inRoles(Role.DocReviewFeatureSet);
	}

	public isLimitedUser(): boolean {
		return this.userConfigsService.inRoles(Role.LimitedFeatureSet);
	}
}
